<template>
  <section
    class="seat-selection"
    v-if="layout && layout.carriages && layout.carriages.length"
  >
    <div
      v-for="(carriage, carriageIndex) in layout.carriages"
      :key="carriageIndex"
      class="carriage-layout"
      :class="activeTab"
    >
      <div v-if="carriage.floorplan_dimensions?.width > 500" class="tabs seat-selection__tabs">
        <button
          type="button"
          @click="activeTab = 'lower-deck'"
          class="tabs-button"
          :class="{ 'tabs-button--active': activeTab === 'lower-deck' || !activeTab }"
        >{{ $t('travelPlanner.seat.firstFloor') }}</button>
        <button
          type="button"
          @click="activeTab = 'upper-deck'"
          class="tabs-button"
          :class="{ 'tabs-button--active': activeTab === 'upper-deck' }"
        >{{ $t('travelPlanner.seat.secondFloor') }}</button>
      </div>
      <CarriageImage
        v-if="carriage.template && carriage.floorplan_dimensions && carriage.floorplan_dimensions.width && carriage.floorplan_dimensions.height"
        :carriageImageSrc="carriage.template"
        :floorplanDimensions="carriage.floorplan_dimensions"
        @imageLoaded="onImageLoad"
        @dimensionsChanged="onImageDimensionsChanged"
      >
        <SeatButton
          v-for="seat in carriage.seats"
          :key="`${carriageIndex}-${seat.sequence_number}`"
          :seat="{ ...seat, status: convertSeatStatus(seat.status) }"
          :isSelected="isSelected(seat)"
          :seatStyle="
            getSeatStyle(seat, carriage.seat_width, carriage.seat_height)
          "
          :seatPrice="getSeatPrice(seat)"
          :service-name="serviceName"
          @selectSeat="selectSeat"
        />
      </CarriageImage>
      <div v-else class="carriage-error">
        <p>Unable to load carriage layout - missing template or dimensions data</p>
      </div>
    </div>
  </section>
  <div v-else>
    <p>Loading carriage layout...</p>
    <p v-if="loadError">Error: {{ loadError }}</p>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, watch, computed, PropType } from 'vue'
  import { CarriageSeat } from '@/types/seatTypes'
  import { CarriageLayout } from '@/types/bookingTypes'
  import CarriageImage from './CarriageImage.vue'
  import SeatButton from './SeatButton.vue'
  import { useSeatReservationStore } from '@/stores/seatReservationStore'

  export default defineComponent({
    name: 'CarriageLayout',
    components: {
      CarriageImage,
      SeatButton,
    },
    props: {
      serviceName: {
        type: String,
        required: true,
      },
      serviceIdentifier: {
        type: String,
        required: true,
      },
      fromStation: {
        type: String,
        required: true,
      },
      toStation: {
        type: String,
        required: true,
      },
      travelDate: {
        type: String,
        required: true,
      },
      selectedSeats: {
        type: Array as PropType<CarriageSeat[]>,
        default: () => [],
      },
      bookingNumber: {
        type: String,
        required: true,
      },
      passengerInfo: {
        type: Array,
        required: true,
      },
      legIndex: {
        type: Number,
        required: true,
      },
      currentSegment: {
        type: Object as PropType<{
          direction: 'outbound' | 'inbound',
          serviceIdentifier: string,
          fromStation: string,
          toStation: string,
          travelDate: string,
          serviceName: string,
          segmentId: string
        }>,
        required: true,
        validator: (value: any) => {
          const isValid = value && 
            value.direction && 
            value.serviceIdentifier && 
            value.fromStation && 
            value.toStation && 
            value.travelDate &&
            value.serviceName &&
            value.segmentId
          return isValid
        }
      },
      seatPrices: {
        type: Object as PropType<{ [key: number]: number }>,
        required: true,
        default: () => ({}),
      },
      carriageLayout: {
        type: Object as PropType<CarriageLayout>,
        required: true,
      }
    },
    emits: ['seatSelected', 'imageLoaded'],
    setup(props, { emit }) {
      const layout = ref(props.carriageLayout)
      const loadError = ref('')
      const isImageLoaded = ref(false)
      const imageDimensions = ref({ width: 0, height: 0 })
      const activeTab = ref('lower-deck')

      // Convert between different seat status formats
      const convertSeatStatus = (status: string | undefined): 'AVAILABLE' | 'ALLOCATED' | 'BLOCKED' => {
        if (!status) return 'AVAILABLE'
        
        // Handle SelectedSeat status values
        if (status === 'confirmed' || status === 'pending') {
          return 'ALLOCATED'
        }
        
        // Handle CarriageSeat status values
        if (status === 'AVAILABLE' || status === 'ALLOCATED' || status === 'BLOCKED') {
          return status
        }
        
        // Default fallback
        return 'AVAILABLE'
      }

      const getSeatPrice = (seat: CarriageSeat) => {
        const seatStore = useSeatReservationStore()
        return seatStore.getSeatPrice(seat, props.currentSegment.direction)
      }

      const onImageLoad = () => {
        layout.value = { ...layout.value }
      }

      const onImageDimensionsChanged = (dimensions: {
        width: number
        height: number
      }) => {
        imageDimensions.value = dimensions
      }

      const selectSeat = (seat: CarriageSeat) => {
        const seatStore = useSeatReservationStore()

        // Emit the event in the format DirectJourney expects
        emit('seatSelected', {
          seat,
          legIndex: props.legIndex,
          direction: props.currentSegment.direction
        })
      }

      const isSelected = computed(() => (seat: CarriageSeat) => {
        const seatStore = useSeatReservationStore()
        const seatKey = seatStore.getSelectedSeatsKey(props.currentSegment.direction, props.legIndex)
        const selectedSeats = seatStore.selectedSeats[seatKey] || []
        
        return selectedSeats.some(
          (selectedSeat) => selectedSeat && selectedSeat.seat_number === seat.seat_number
        )
      })
      
      const getSeatStyle = (
        seat: CarriageSeat,
        seatWidth: number,
        seatHeight: number
      ): Record<string, string> => {
        return {
          position: 'absolute',
          left: `${seat.x_pos}px`,
          top: `${seat.y_pos}px`,
          width: `${seatWidth}px`,
          height: `${seatHeight}px`,
        }
      }

      return {
        layout,
        loadError,
        isImageLoaded,
        imageDimensions,
        onImageLoad,
        onImageDimensionsChanged,
        getSeatStyle,
        isSelected,
        selectSeat,
        getSeatPrice,
        activeTab,
        convertSeatStatus,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .seat-selection {
    width: 100%;
    position: relative;

    &__tabs {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: flex-end;
      margin-top: 0;

      @media (min-width: $breakpoint-xl) {
        display: none;
      }
    }
  }

  .tab-header + .tab-content .seat-selection__tabs::before {
    content: '';
    position: absolute;
    top: -20px;
    right: 20px;
    left: 20px;
    border-top: 1px solid $clr-primary-100;
  }

  .carriage-layout {
    position: relative;
    margin: 0 auto;
    width: 100%;

    @media (max-width: ($breakpoint-xl - 1px)) {
      &.lower-deck,
      &.upper-deck {
        width: 310px;
      }

      &.lower-deck {
        .carriage-layout__wrapper {
          justify-content: flex-start;
          width: 310px;
        }
      }

      &.upper-deck {
        .carriage-layout__wrapper {
          justify-content: flex-end;
          width: 310px;
        }
      }
    }
  }

  :deep(.seat-button) {
    transform-origin: center;
  }

  .carriage-error {
    padding: 2rem;
    text-align: center;
    border-radius: 8px;
    margin: 1rem 0;

    p {
      margin: 0;
      @include body-medium;
    }
  }
</style>